import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

interface SchemeConditionsProps {
  analysis: any;
}

export const SchemeConditions: React.FC<SchemeConditionsProps> = ({ analysis }) => {
  if (!analysis) return null;

  // Check for critical failing conditions
  const failedConditions = analysis.final_decision?.failed_conditions || [];
  const hasCriticalFailures = failedConditions.some((condition: string) =>
    condition === 'missing_covid_reporting_data' ||
    condition === 'outside_covid_notice_date_range'
  );

  // Also check the actual condition objects
  const dateRangeFailing = analysis.date_range_condition && !analysis.date_range_condition.passes;
  const missingDataFailing = analysis.missing_covid_reporting_data && !analysis.missing_covid_reporting_data.passes;

  // If any critical conditions are failing, only show those
  if (hasCriticalFailures || dateRangeFailing || missingDataFailing) {
    const criticalConditions: React.ReactNode[] = [];

    // Check for missing COVID reporting data
    if (failedConditions.includes('missing_covid_reporting_data') || missingDataFailing) {
      criticalConditions.push(
        <Chip
          key="missing_covid_reporting_data"
          label="Missing COVID Reporting Data"
          size="small"
          color="error"
          variant="filled"
          sx={{
            justifyContent: 'flex-start',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        />
      );
    }

    // Check for outside COVID notice date range
    if (failedConditions.includes('outside_covid_notice_date_range') || dateRangeFailing) {
      criticalConditions.push(
        <Chip
          key="outside_covid_notice_date_range"
          label="Outside COVID Notice Date Range"
          size="small"
          color="error"
          variant="filled"
          sx={{
            justifyContent: 'flex-start',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        />
      );
    }

    return (
      <Box sx={{ width: 'fit-content', minWidth: 200 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Scheme Conditions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {criticalConditions}
        </Box>
      </Box>
    );
  }

  // Otherwise, show all conditions except the excluded ones
  // Define sorting order for scheme conditions
  const conditionOrder = [
    'occupancy_condition',
    'date_range_condition',
    'post_cutoff_condition',
    'covid_condition',
    'covid_deaths_condition',
    'missing_covid_reporting_data'
  ];

  const conditions = Object.entries(analysis)
    .filter(([conditionName]) =>
      // Exclude metadata fields and covid_absolute_condition (not ready for display yet)
      !['eligibility', 'final_decision', 'data_availability', 'covid_absolute_condition'].includes(conditionName)
    )
    .sort(([conditionNameA], [conditionNameB]) => {
      const indexA = conditionOrder.indexOf(conditionNameA);
      const indexB = conditionOrder.indexOf(conditionNameB);

      // If both conditions are in the order array, sort by their order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in the order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is in the order array, sort alphabetically
      return conditionNameA.localeCompare(conditionNameB);
    })
    .map(([conditionName, condition]: [string, any]) => {
      // Map condition names to custom text
      const conditionTextMap: { [key: string]: string } = {
        'occupancy_condition': 'Admit Count > 10% of residents',
        'date_range_condition': 'After July 21, 2020',
        'post_cutoff_condition': 'Admit Count greater than any date after 5/11/2023',
        'covid_condition': 'Admit Count 4x greater than max COVID report +/- 21 days',
        'covid_deaths': 'No COVID deaths +/- 21 Days',
        'covid_deaths_condition': 'No COVID deaths +/- 21 Days',
        'missing_covid_reporting_data': 'Missing COVID Reporting Data'
      };

      const displayText = conditionTextMap[conditionName] || conditionName.replace('_', ' ');

      return (
        <Chip
          key={conditionName}
          label={displayText}
          size="small"
          color={condition?.passes ? 'success' : 'error'}
          variant="filled"
          sx={{
            justifyContent: 'flex-start',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        />
      );
    });

  return (
    <Box sx={{ width: 'fit-content', minWidth: 200 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Scheme Conditions
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {conditions}
      </Box>
    </Box>
  );
};